import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { systemService } from "../../services/api";
import Button from "../common/Button";
import {
  describeValue,
  fieldLabel,
  formatLogTimestamp,
  personLabel,
} from "./systemLogPresentation";

const Muted = styled.div`
  font-size: 13px;
  color: #53616c;
  line-height: 1.5;
  overflow-wrap: anywhere;
`;
const Notice = styled.div`
  padding: 12px;
  border-radius: 4px;
  background: #eef6f9;
  color: #244653;
  margin: 12px 0;
  overflow-wrap: anywhere;
`;
const Detail = styled.div`
  padding: 16px;
  border: 1px solid #adc7d3;
  border-radius: 4px;
  background: #fbfdfe;
  min-width: 0;
  h3,
  h4 {
    margin-top: 0;
  }
  h4 {
    margin-top: 20px;
  }
  dl {
    display: grid;
    grid-template-columns: minmax(100px, 160px) minmax(0, 1fr);
    gap: 8px;
    margin: 12px 0;
  }
  dt {
    font-weight: 600;
  }
  dd {
    margin: 0;
    overflow-wrap: anywhere;
  }
  pre {
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    max-height: 320px;
    overflow: auto;
    font-size: 12px;
  }
  @media (max-width: 600px) {
    padding: 10px;
    dl {
      grid-template-columns: 1fr;
      gap: 4px;
    }
    dd {
      margin-bottom: 8px;
    }
  }
`;
const SnapshotGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  @media (max-width: 700px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
const Scroll = styled.div`
  overflow-x: auto;
  max-width: 100%;
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  th,
  td {
    padding: 10px;
    border-bottom: 1px solid #dce5e9;
    text-align: left;
    vertical-align: top;
  }
  th {
    background: #eef4f6;
  }
  td {
    overflow-wrap: anywhere;
  }
`;
const LinkButton = styled.button`
  border: 0;
  background: none;
  color: #19617e;
  text-decoration: underline;
  padding: 6px 0;
  cursor: pointer;
  font: inherit;
  text-align: left;
  display: block;
`;

export const LogActor = ({ log }) => (
  <>
    <strong>
      {personLabel(log.actor, log.user || "System / unknown account")}
    </strong>
    {log.actor?.name &&
      log.actor?.email &&
      log.actor.name !== log.actor.email && <Muted>{log.actor.email}</Muted>}
    {log.impersonated && (
      <>
        <Muted>
          <strong>Support acting on behalf of user</strong>
        </Muted>
        <Muted>Using account: {personLabel(log.effectiveUser)}</Muted>
      </>
    )}
    {log.actor?.role && <Muted>{log.actor.role}</Muted>}
  </>
);
export const LogSubject = ({ log }) => (
  <>
    {log.employee && (
      <div>{log.employee.name || `Employee #${log.employee.id}`}</div>
    )}
    {log.customer && (
      <div>{log.customer.name || `Customer #${log.customer.id}`}</div>
    )}
    {log.workDate && <Muted>Workday: {log.workDate}</Muted>}
    {log.timeEntryId && <Muted>Entry #{log.timeEntryId}</Muted>}
  </>
);

function Snapshot({
  title,
  value,
  missing,
  units,
  empty = "No previous entry.",
}) {
  return (
    <div>
      <h4>{title}</h4>
      {value === undefined ? (
        <Muted>{missing || "Not recorded in this event."}</Muted>
      ) : value === null ? (
        <Muted>{empty}</Muted>
      ) : (
        <dl>
          {Object.entries(value).map(([key, item]) => (
            <React.Fragment key={key}>
              <dt>{fieldLabel(key)}</dt>
              <dd>
                {describeValue(item, key, { units, currency: value.currency })}
              </dd>
            </React.Fragment>
          ))}
        </dl>
      )}
    </div>
  );
}

export default function LogEvidence({ id, onRequest, onEntry }) {
  const [state, setState] = useState({ loading: true });
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setState({ loading: true });
    systemService
      .getSystemLog(id, { signal: controller.signal })
      .then((response) => {
        if (active) setState({ ...response.data, loading: false });
      })
      .catch(() => {
        if (active) setState({ loading: false, error: true });
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [id, retry]);
  if (state.loading)
    return <Detail role="status">Loading evidence for log #{id}…</Detail>;
  if (state.error || !state.log)
    return (
      <Detail>
        <Notice role="alert">
          Unable to load log #{id}. Your access may have changed or the log may
          no longer be available.
        </Notice>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setRetry((value) => value + 1)}
        >
          Retry details
        </Button>
      </Detail>
    );
  const log = state.log;
  const metadata = log.metadata || {};
  const limitations = [
    ...new Set([
      ...(log.evidenceLimitations || []),
      ...(state.limitations || []),
    ]),
  ];
  const beforeRecorded =
    Object.prototype.hasOwnProperty.call(metadata, "before") ||
    (log.before !== undefined && log.before !== null);
  const afterRecorded =
    Object.prototype.hasOwnProperty.call(metadata, "after") ||
    (log.after !== undefined && log.after !== null);
  const before = beforeRecorded
    ? log.before !== undefined
      ? log.before
      : metadata.before
    : undefined;
  const after = afterRecorded
    ? log.after !== undefined
      ? log.after
      : metadata.after
    : undefined;
  const related = state.relatedLogs || [];
  return (
    <Detail aria-label={`Evidence for log ${id}`}>
      <h3>Evidence · Log #{log.id}</h3>
      <p>{log.summary || log.message}</p>
      <dl>
        <dt>Recorded at</dt>
        <dd>
          {formatLogTimestamp(log.timestamp)}
          <Muted>UTC: {log.timestamp}</Muted>
        </dd>
        <dt>Acting account</dt>
        <dd>
          <LogActor log={log} />
        </dd>
        <dt>Subject</dt>
        <dd>
          <LogSubject log={log} />
        </dd>
        <dt>Action</dt>
        <dd>{log.action || "Not recorded"}</dd>
        <dt>Outcome</dt>
        <dd>
          {log.outcome ||
            metadata.outcome ||
            metadata.status ||
            (log.level === "error" ? "Error recorded" : "See recorded event")}
        </dd>
      </dl>
      {log.actor?.source && <Muted>Attribution: {log.actor.source}</Muted>}
      {log.actor?.id && <Muted>Acting user ID: {log.actor.id}</Muted>}
      {log.requestId && (
        <>
          <Muted>
            Request: <code>{log.requestId}</code>
          </Muted>
          <LinkButton onClick={() => onRequest(log.requestId)}>
            Show all events for this request
          </LinkButton>
        </>
      )}
      {log.timeEntryId && (
        <LinkButton onClick={() => onEntry(log.timeEntryId)}>
          Show full recorded history for entry #{log.timeEntryId}
        </LinkButton>
      )}
      {log.changeSummary && <p>{log.changeSummary}</p>}
      {log.changes?.length > 0 && (
        <Scroll>
          <Table aria-label="Recorded changes">
            <thead>
              <tr>
                <th>Changed field</th>
                <th>Before</th>
                <th>After</th>
              </tr>
            </thead>
            <tbody>
              {log.changes.map((change, index) => (
                <tr key={`${change.field}-${index}`}>
                  <td>{fieldLabel(change.field)}</td>
                  <td>
                    {describeValue(change.before, change.field, {
                      units: metadata.units,
                      currency: before?.currency,
                    })}
                  </td>
                  <td>
                    {describeValue(change.after, change.field, {
                      units: metadata.units,
                      currency: after?.currency,
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Scroll>
      )}
      {(beforeRecorded || afterRecorded || log.type === "timereporting") && (
        <SnapshotGrid>
          <Snapshot
            title="Before this action"
            value={before}
            units={metadata.units}
            missing="Previous values were not recorded. Do not assume that this was the first save."
          />
          <Snapshot
            title="After this action"
            value={after}
            units={metadata.units}
            empty="No entry after this action."
          />
        </SnapshotGrid>
      )}
      {log.type === "timereporting" &&
        !beforeRecorded &&
        !limitations.length && (
          <Notice>
            Historical event: the full previous state was not recorded. This
            event cannot establish all earlier start times, end times or breaks.
          </Notice>
        )}
      {limitations.map((text, index) => (
        <Notice key={index}>{text}</Notice>
      ))}
      <h4>Related events · oldest first</h4>
      <Muted>
        Events sharing the request or time entry provide context. An account
        identity alone does not prove which person used it.
      </Muted>
      {related.length ? (
        <Scroll>
          <Table aria-label="Related events">
            <thead>
              <tr>
                <th>Recorded at / log</th>
                <th>Acting account</th>
                <th>Event</th>
              </tr>
            </thead>
            <tbody>
              {related.map((item) => (
                <tr key={item.id}>
                  <td>
                    {formatLogTimestamp(item.timestamp)}
                    <Muted>Log #{item.id}</Muted>
                  </td>
                  <td>
                    <LogActor log={item} />
                  </td>
                  <td>
                    {item.summary || item.message}
                    <Muted>
                      {item.requestId && item.requestId === log.requestId
                        ? "Same request"
                        : "Same time entry"}
                    </Muted>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Scroll>
      ) : (
        <p>No related events were recorded or are available.</p>
      )}
      {state.relatedPagination?.truncated && (
        <Notice>
          Showing {state.relatedPagination.returned} of{" "}
          {state.relatedPagination.total} related events. Use the full history
          or request search above to page through the remaining events.
        </Notice>
      )}
      <details style={{ marginTop: 16 }}>
        <summary>Recorded technical details</summary>
        <p>{log.message}</p>
        <pre>{JSON.stringify(metadata, null, 2)}</pre>
      </details>
    </Detail>
  );
}
